import * as React from 'react';
import { Box, SimpleGrid } from '@chakra-ui/react';
import {
  HStack,
  Text,
  Tooltip,
  VStack,
} from '@ayble-health/dashboard-components';
import { Chip, type ChipVariant } from 'components/ui/chip';
import { AUDIT_BADGE_CHROME } from './ChangeBadge';
import {
  COMPONENT_PARITY,
  PARITY_COMPARED_AGAINST,
  PARITY_COMPARED_ON,
  PARITY_STATUSES,
  parityCounts,
  type ParityName,
  type ParityStatus,
} from './parity';

/**
 * Chrome per status. `mint` reads as settled, `feather` as settled-with-an-
 * asterisk, `canvas` as neutral (a careOS-only component isn't a problem, it's
 * out of scope for the merge), and `ballet` as needs-attention — the same
 * pairing the roster uses.
 */
const STATUS_CHROME: Record<
  ParityStatus,
  { label: string; variant: ChipVariant }
> = {
  shared: { label: 'Shared', variant: 'mint' },
  patched: { label: 'Shared + patch', variant: 'neutral' },
  local: { label: 'careOS only', variant: 'canvas' },
  'package-only': { label: 'Available, unused', variant: 'ballet' },
};

const STATUS_MEANING: Record<ParityStatus, string> = {
  shared:
    'Imported from the package. Either the code was already equivalent, or the package was ahead and we took its version.',
  patched:
    'The package component, wrapped locally to carry a documented fix the package does not have yet.',
  local: 'No equivalent in the package. Stays in this repo either way.',
  'package-only':
    'The package exports it and we do not consume it yet. Adopting it is a separate decision.',
};

/**
 * Marks a specimen with where the component now comes from.
 *
 * Sits alongside `ChangeBadge` rather than replacing it: that one answers "when
 * did this move?", this one answers "is this ours, theirs, or theirs-with-a-
 * patch?" — the question the reconciliation turns on.
 *
 * The `name` is typed to the parity record, so a badge can't reference a
 * component nobody recorded a claim for.
 */
export const ParityBadge = ({ name }: { name: ParityName }) => {
  const { status, note } = COMPONENT_PARITY[name];
  const { label, variant } = STATUS_CHROME[status];

  const tag = (
    <Chip variant={variant} {...AUDIT_BADGE_CHROME}>
      {label}
    </Chip>
  );

  // Plain `shared` entries carry no note — a tooltip on every green tag would
  // be noise. Everything else explains itself on hover.
  //
  // The `<span>` is load-bearing. `Tooltip.Trigger` uses `asChild`, which clones
  // its child and forwards trigger props onto it; cloning `Tag` directly (a
  // `chakra('span', recipe)` component) throws inside Chakra. Wrapping a plain
  // element means the clone target is an ordinary span and the recipe component
  // is just its child. Verified against v1.2.1 — the package's own Tooltip is
  // fine when its child is a plain or non-recipe component.
  return note ? (
    <Tooltip label={note}>
      <span>{tag}</span>
    </Tooltip>
  ) : (
    tag
  );
};

/**
 * Rendered once above the atoms grid. Without it the badges are ambiguous — a
 * reader can't tell whether an untagged component is shared, undecided, or
 * simply unrecorded, and can't know what the claims were measured against.
 */
export const ParityLegend = () => {
  const counts = parityCounts();

  return (
    <Box borderWidth="1px" borderColor="cloud" rounded="lg" p={4} bg="white">
      <VStack gap={3} align="stretch">
        <VStack gap={1} align="stretch">
          <Text fontSize="sm" fontWeight="bold" color="muted.900">
            Shared-package parity
          </Text>
          <Text fontSize="sm" color="muted.600">
            Where each atom and molecule comes from now that this repo consumes{' '}
            <Text as="span" fontFamily="mono" fontSize="xs">
              @ayble-health/dashboard-components
            </Text>{' '}
            (ENG-63). See the <strong>Repo</strong> tab for the full upstream
            surface.
          </Text>
        </VStack>

        <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} gap={3}>
          {PARITY_STATUSES.map((status) => (
            <VStack key={status} gap={1} align="flex-start">
              <HStack gap={2} align="center">
                <Chip
                  variant={STATUS_CHROME[status].variant}
                  {...AUDIT_BADGE_CHROME}
                >
                  {STATUS_CHROME[status].label}
                </Chip>
                <Text fontSize="xs" color="muted.400">
                  {counts[status]}
                </Text>
              </HStack>
              <Text fontSize="xs" color="muted.400">
                {STATUS_MEANING[status]}
              </Text>
            </VStack>
          ))}
        </SimpleGrid>

        <Box borderTopWidth="1px" borderColor="cloud" pt={3}>
          <Text fontSize="xs" color="muted.400">
            Verified against {PARITY_COMPARED_AGAINST} on {PARITY_COMPARED_ON} —
            the package itself, cloned and diffed file by file against the local
            copies as they stood on <code>44713ab</code>. Both sides were
            prettier-normalized and comment-stripped first, because the package
            condenses doc comments: of the 28 shared files, a raw diff flags all
            28 while only 13 differ in code. Hover a tag for the specifics.
          </Text>
        </Box>
      </VStack>
    </Box>
  );
};
